import { useCallback } from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { MdOutlineAddCircle, MdOutlineDelete } from 'react-icons/md';
import { Button, TmdxSelect, SelectItem, TmdxAvatar, IconButton, CheckboxGroup, Checkbox } from '@ds';
import { CCSchedulerSettingsRulesFormType } from '@lp/features/Scheduler/SchedulerClinicalCoordinatorsSettings/types';
import { CC_SHIFT_TYPE_SELECT_LIST } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/constants';
import { CCResource } from '@/api/apis/resources/schedule';
import { getFullName, renderSelectItem } from '@/utils/helpers';
import { WEEKDAYS } from '@/constants/date.constants';

type CCSettingsCustomRulesListFormProps = {
  userList: CCResource[];
};

export const CCSettingsCustomRulesListForm = ({ userList }: CCSettingsCustomRulesListFormProps) => {
  const { control } = useFormContext<CCSchedulerSettingsRulesFormType>();
  const { fields, append, remove } = useFieldArray<CCSchedulerSettingsRulesFormType>({ control, name: 'customRules' });

  const onCustomRuleCreate = useCallback(() => {
    append({
      resourceId: '',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      status: '',
      dayOfWeeks: [],
    });
  }, [append]);

  return (
    <div className="flex flex-col gap-4 w-full p-6 bg-white rounded-2xl shadow-s">
      <div className="flex items-center justify-between gap-2">
        <span className="text-base-5 text-xl-sb">Custom Rules</span>
        <Button
          disableRipple
          size="sm"
          variant="light"
          endContent={<MdOutlineAddCircle />}
          onClick={onCustomRuleCreate}
        >
          Add New Rule
        </Button>
      </div>

      <div className="flex flex-col gap-5">
        {fields.length === 0 && <span className="text-base-30 text-l-r">No custom rules were created</span>}

        {fields.map((cr, index) => (
          <div key={cr.id} className="flex items-center gap-6">
            <span className="text-base-5 text-m-sb">{index + 1}.</span>

            <Controller
              name={`customRules.${index}.resourceId`}
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <TmdxSelect
                  aria-label="Custom rule select"
                  selectionMode="single"
                  placeholder="Clinical Coordinator"
                  items={userList}
                  selectedKeys={value ? [value] : []}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  {...field}
                  renderValue={(items) => {
                    return items.map((item) => {
                      const fullName = getFullName(item.data?.firstName, item.data?.lastName);

                      return (
                        <div key={item.key} className="flex items-center gap-2">
                          <TmdxAvatar size="md" radius="md" src={item.data?.profilePhoto} name={fullName} />
                          <span>{fullName}</span>
                        </div>
                      );
                    });
                  }}
                >
                  {(user) => {
                    const fullName = getFullName(user.firstName, user.lastName);

                    return (
                      <SelectItem key={user.id} textValue={fullName}>
                        <div className="flex items-center gap-2">
                          <TmdxAvatar size="md" radius="md" src={user.profilePhoto} name={fullName} />
                          <span>{fullName}</span>
                        </div>
                      </SelectItem>
                    );
                  }}
                </TmdxSelect>
              )}
            />

            <Controller
              name={`customRules.${index}.status`}
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <TmdxSelect
                  aria-label="Custom rule select"
                  selectionMode="single"
                  placeholder="Status"
                  items={CC_SHIFT_TYPE_SELECT_LIST}
                  selectedKeys={value ? [value] : []}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  {...field}
                >
                  {renderSelectItem}
                </TmdxSelect>
              )}
            />

            <Controller
              name={`customRules.${index}.dayOfWeeks`}
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <CheckboxGroup
                  orientation="horizontal"
                  aria-label="Select custom rule days"
                  value={value || []}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  classNames={{ wrapper: 'flex-nowrap' }}
                  {...field}
                >
                  {WEEKDAYS.map((day) => (
                    <Checkbox key={day} size="sm" value={day}>
                      {day.substring(0, 3)}
                    </Checkbox>
                  ))}
                </CheckboxGroup>
              )}
            />

            <IconButton
              disableRipple
              size="sm"
              variant="light"
              startContent={<MdOutlineDelete />}
              onClick={() => remove(index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
